import styled, { useTheme } from 'styled-components/macro';
import { BigLabel, Label } from '../../SubComponents/Text';
import { Button } from '../../SubComponents/Buttons';
import { Image } from '../../SubComponents/Media';
import { DockingLocationProps } from '.';
import useFormatPrice from '../../Hooks/use-format-price';
import { DockingLocationAddress } from './default-docking-location';
import useTypeSafeLocalize from '../../Hooks/use-type-safe-localize';
import { LocalizationCategory, ProductLocalization } from '../../localizations/types';

const DockingLocationContainer = styled.div<{ selected: boolean }>`
  width: 100%;
  min-height: 160px;
  display: flex;
  background-color: ${(props) =>
    props.selected ? props.theme.colors.buttons.primary.backgroundColor : '#fff'};
  border-radius: 4px;
`;

const DockingLocationImageContainer = styled.div`
  position: relative;
  width: min(42%, 160px);
  height: 160px;
  background-color: #f1f3f6;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: min(42%, 300px);
  }
`;

const DockingLocationImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
`;

const DockingLocationImageButton = styled.div`
  font-family: 'Faro-Regular';
  font-size: 1.2rem;
  color: #182031;
  background-color: rgba(255, 255, 255, 0.6);
  border-top-left-radius: 4px;
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 4px 8px;
  display: flex;
  align-items: center;
`;

const DockingLocationContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  padding: 16px;
`;

const DockingLocationTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const DockingLocationButton = styled(Button)<{ selected: boolean }>`
  ${(props) => (props.selected ? `background-color: ${props.theme.highLightColor}` : '')};
  ${(props) => (props.selected ? `color: ${props.theme.textColor}` : '')};
`;

const DockingLocation = (props: DockingLocationProps) => {
  const localize = useTypeSafeLocalize();
  const formatPrice = useFormatPrice();
  const theme = useTheme();

  if (!props.dockingLocation?.id) return null;

  const priceLow = props.dockingLocation.priceRange.low * (props.boatCount ?? 1);
  const priceHigh = props.dockingLocation.priceRange.high * (props.boatCount ?? 1);
  const priceRange =
    priceLow === priceHigh
      ? formatPrice(priceLow)
      : `${formatPrice(priceLow)} - ${formatPrice(priceHigh)}`;

  return (
    <DockingLocationContainer selected={props.selected}>
      <DockingLocationImageContainer>
        <a
          href={props.mapLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <DockingLocationImage
            src={props.dockingLocation.image.src}
            alt={props.dockingLocation.image.alt}
            lazyload
          />
          <DockingLocationImageButton>
            {localize(LocalizationCategory.product, ProductLocalization.viewOnMap)}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#182031"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-map-pin"
              style={{ marginLeft: '4px' }}
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </DockingLocationImageButton>
        </a>
      </DockingLocationImageContainer>

      <DockingLocationContentContainer>
        <a
          href={props.mapLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <DockingLocationTextContainer>
            <Label
              color={props.selected ? '#fff' : theme.textColor}
              style={{ cursor: 'pointer' }}
            >
              {props.dockingLocation.name}
            </Label>
            <DockingLocationAddress selected={props.selected}>
              {props.dockingLocation.address}
            </DockingLocationAddress>
          </DockingLocationTextContainer>
        </a>

        <BigLabel color={props.selected ? '#fff' : theme.textColor}>
          {priceRange}
        </BigLabel>

        <DockingLocationButton
          selected={props.selected}
          onClick={() =>
            props.dockingLocation ? props.setSelected(props.dockingLocation.id) : null
          }
        >
          {props.selected
            ? `${localize(LocalizationCategory.product, ProductLocalization.selected)} ✓`
            : localize(LocalizationCategory.product, ProductLocalization.select)}
        </DockingLocationButton>
      </DockingLocationContentContainer>
    </DockingLocationContainer>
  );
};

export default DockingLocation;
